<template>
  <div
    :class="['banner-placeholder', variantClass]"
    :style="styles"
  >
    <img
      v-if="variant === 'primary'"
      :alt="alt"
      :src="image"
      :class="`banner-placeholder--${variant}__image`"
    >
    <div>
      <c-kicker
        v-if="variant === 'tertiary'"
        color="#717D96"
        weight="700"
        align="left"
      >
        <slot name="kicker" />
      </c-kicker>

      <c-title
        v-if="variant !== 'primary'"
        :color="titleColor"
        size="h2"
        weight="400"
        :class="`banner-placeholder--${variant}__title`"
      >
        <slot name="title" />
      </c-title>
    </div>
    <div :class="`banner-placeholder--${variant}__container`">
      <p
        :class="`banner-placeholder--${variant}__text`"
        :style="{ color: textColor }"
      >
        <slot name="text" />
      </p>

      <div
        :class="`banner-placeholder--${variant}__slot`"
        @click="pushGTM"
      >
        <slot />
      </div>
    </div>

    <div
      v-if="variant === 'quaternary'"
      :class="`banner-placeholder--${variant}__links`"
      @click="pushGTM"
    >
      <a
        v-for="(item, index) in bannerItems"
        :key="index"
        :href="item.href"
        :class="`banner-placeholder--${variant}__link`"
      >
        <c-image
          :alt="item.alt"
          :image-mobile="item.imageMobile"
          :image-tablet="item.imageTablet"
          :image-desktop="item.imageDesktop"
          max-size="260"
          :background="false"
        />
        {{ item.description }}
      </a>
    </div>
  </div>
</template>

<script>
import { useSlots, computed } from 'vue'
import CTitle from '@atoms/title/title.vue'
import CKicker from '@atoms/kicker/kicker.vue'
import CImage from '@atoms/image/image.vue'
import isOutbound from '@tagging/isOutbound.js'

export default {
  name: 'banner-placeholder',
  components: { CTitle, CKicker, CImage },
  props: {
    variant: {
      type: String,
      default: 'primary',
    },
    image: {
      type: String,
    },
    alt: {
      type: String,
    },
    bgColor: {
      type: String,
      default: 'transparent',
    },
    titleColor: {
      type: String,
      default: '#1A202C',
    },
    textColor: {
      type: String,
      default: '#43515A',
    },
    bannerItems: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const styles = computed(() => ({
      backgroundColor: props.bgColor,
    }))

    const variantClass = computed(() => {
      return props.variant ? `banner-placeholder--${props.variant}` : ''
    })

    const title = useSlots().title?.()[0]?.children

    const pushGTM = (event) => {
      const target = event.target.closest('a') || event.target

      if (target && target.tagName === 'A') {
        window.dataLayer.push({
          event: 'click',
          section: title || '',
          outbound: isOutbound(target),
          label: target.innerText || ''
        })
      }
    }

    return {
      styles,
      variantClass,
      pushGTM
    }
  },
}
</script>

<style lang="scss">
.banner-placeholder {
  h1 {
    @include h1();
  }

  h2 {
    @include h2();
  }

  h3 {
    @include h3();
  }

  @mixin placeholder-text() {
    font-size: 1.125rem;
    line-height: 150%;
  }

  @mixin display-grid() {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    align-items: center;
    justify-content: center;

    @include screen-size("xl") {
      column-gap: 4.277rem;
    }
  }

  @mixin display-flex() {
    display: flex;
    flex-direction: column;
  }

  &--primary {
    @include display-flex();
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;

    @include screen-size("xlg") {
      flex-direction: row;
      gap: 2.5rem;
    }

    &__image {
      width: 100%;
      max-width: 17rem;
      object-fit: cover;

      @include screen-size("xlg") {
        max-width: 25.5rem;
      }
    }

    &__text {
      font-size: 0.875rem;
      line-height: 160%;
      font-weight: 400;

      @include screen-size("xlg") {
        font-size: 1.25rem;
      }
    }
  }

  &--secondary {
    @include screen-size("xlg") {
      @include display-grid();
    }

    &__container {
      @include display-flex();
      align-items: flex-start;
      gap: 1.5rem;
      margin-top: 1.125rem;

      @include screen-size("xlg") {
        align-content: flex-end;
        align-items: flex-end;
        text-align: end;
        gap: 1rem;
        margin-top: unset;
      }
    }

    &__text {
      @include placeholder-text();
    }
  }

  &--tertiary {
    @include display-flex();
    gap: 0.5rem;

    @include screen-size("xlg") {
      @include display-grid();
    }

    &__container {
      @include screen-size("xlg") {
        grid-area: 1 / 2 / 2 / 2;
      }
    }

    &__text {
      margin-top: 0.5rem;
      @include placeholder-text();

      @include screen-size("xlg") {
        margin-top: unset;
        text-align: end;
      }
    }
  }

  &--quaternary {
    @include screen-size("xlg") {
      @include display-grid();
      justify-items: end;
      gap: 1rem;
    }

    &__text {
      margin-top: 0.5rem;
      @include placeholder-text();

      @include screen-size("xlg") {
        margin-top: unset;
      }
    }

    &__links {
      @include display-grid();
      gap: 2rem;
      padding-top: 3.5rem;
      max-width: fit-content;
      margin: 0 auto;

      @include screen-size("md") {
        display: flex;
      }

      @include screen-size("xlg") {
        padding-top: unset;
        grid-area: 1 / 2 / 3 / 3;
        margin: unset;
      }
    }

    &__link {
      @include display-flex();
      gap: 0.5rem;
      font-size: 1rem;
      font-weight: 700;
      color: $neutral-00;
      text-decoration: none;
      text-align: center;
    }
  }

  &--quinary {
    @include display-flex();
    gap: 1rem;
    padding: 3.25rem 2rem 4.75rem;

    @include screen-size("xlg") {
      @include display-grid();
      padding: 4rem 7rem;
    }

    &__container {
      @include screen-size("xlg") {
        @include display-flex();
        align-items: flex-end;
      }
    }

    &__text {
      margin-bottom: 1.5rem;
      @include placeholder-text();

      @include screen-size("xlg") {
        margin-bottom: 0.5rem;
        text-align: end;
      }
    }
  }

  &--senary {
    @include display-flex();
    gap: 0.5rem;
    padding: 3.25rem 2rem 4.75rem;
    text-align: center;

    @include screen-size("xlg") {
      @include display-grid();
      padding: 4rem 7rem;
      text-align: left;
    }

    &__title {
      text-align: center !important;
      justify-content: center !important;

      @include screen-size("xlg") {
        text-align: left !important;
      }
    }

    &__container {
      @include display-flex();
      gap: 1rem;

      @include screen-size("xlg") {
        gap: 2rem;
      }
    }

    &__text {
      @include placeholder-text();
    }

    &__slot {
      @include display-flex();
      align-items: center;
      gap: 1rem;

      @include screen-size("xlg") {
        flex-direction: row;
      }
    }
  }

  &--septenary {
    @include display-flex();
    gap: 1rem;
    padding: 3.25rem 2rem 4.75rem;

    @include screen-size("xlg") {
      @include display-grid();
      justify-items: center;
      padding: 4rem 7rem;
    }

    &__title {
      text-align: left !important;

      @include screen-size("xlg") {
        text-align: center !important;
      }
    }

    &__container {
      @include screen-size("xlg") {
        grid-area: 1 / 1 / 2 / 2;
      }
    }

    &__text {
      margin-bottom: 1.5rem;
      @include placeholder-text();
    }

    &__slot {
      @include display-flex();
      gap: 1rem;

      @include screen-size("xlg") {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
      }

      > div {
        max-width: unset;
        margin-bottom: unset !important;
      }

      > div > div > span, div > div {
        display: flex;
        color: $blue-03 !important;
        font-weight: 800;
      }
    }
  }
}
</style>
